import { echarts } from '@/standardChartManager/standardChart';
import {
  type Layer,
  type StandardChartLayerSpec,
  defineStandardChartLayer
} from '../../../core/layer';
import DEFINE from '../../../util/DEFINE';

const visInfo: Layer['visInfo'] = {
  id: 'chainChangeBar',
  name_en: 'chainChangeBar',
  // name_zh: '环比变化柱状图',
  types_en: [],
  purpose_en: [],
  purpose_zh: [],
  standers: '',
  rules: {
    x: amount => amount > 1,
    y: amount => amount > 1,
    y1: amount => amount > 1
  },
  /** 保证和 Spec.encoding 字段一致 */
  parameters: {
    x: {
      itemType: [DEFINE.STR],
      category: DEFINE.SINGLE
    },
    y: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    },
    y1: {
      itemType: [DEFINE.NUMBER],
      category: DEFINE.SINGLE
    }
  }
};

/**
 * ECharts 的配置项
 * @see https://echarts.apache.org/zh/option.html#series-line
 */
// type EChartsPieSeries = {
//   roseType?: unknown;
//   radius?: unknown;
// };

export interface Spec extends StandardChartLayerSpec {
  encoding: {
    x: string;
    y: string;
    y1: string;
  };
}
/**
 *  RGBA颜色（包含透明度）处理成不带透明度的rgb颜色，保证rgba的颜色和rgb的颜色看起来一样
 * @returns
 */
function rgbaToEquivalentRgb(rgbaString: string): string {
  // 用正则表达式提取RGBA颜色字符串中的颜色通道和透明度值
  const rgbaRegex = /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/;
  const match = rgbaString.match(rgbaRegex);

  if (!match) {
    throw new Error('Invalid color format');
  }

  // 提取颜色通道和透明度值
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  const a = parseFloat(match[4]);

  // 假设背景色为白色 (255, 255, 255)
  const R = 255;
  const G = 255;
  const B = 255;

  // 计算不带透明度的RGB颜色，保持视觉一致
  const R_prime = Math.round(r * a + R * (1 - a));
  const G_prime = Math.round(g * a + G * (1 - a));
  const B_prime = Math.round(b * a + B * (1 - a));

  // 构建新的RGB颜色字符串
  return `rgb(${R_prime}, ${G_prime}, ${B_prime})`;
}

export const chainChangeBar = defineStandardChartLayer<Spec>({
  type: visInfo.id,
  isIndependentLayer: true,
  visInfo,
  render(api, spec, $dom) {
    const { data: sourceData } = api;
    const {
      dataIndex = 0,
      encoding: { x, y, y1 }
    } = spec;

    const yAxis = sourceData[dataIndex].values.map(item => item[y]);
    const xAxis = sourceData[dataIndex].values.map(item => item[x]);
    const customData = sourceData[dataIndex].values.map((item, index) => {
      return [index, item[y], item[y1]];
    });

    /**
     *  配置Series
     * @returns
     */
    function configureSeriesOption() {
      return {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          }
        },
        xAxis: [
          {
            show: true,
            type: 'category',
            data: xAxis,
            axisPointer: {
              show: true,
              type: 'shadow',
              shadowStyle: {
                color: 'rgba(172,198,251)',
                opacity: 0.3
              }
            }
          }
        ],
        yAxis: [
          {
            show: true,
            type: 'value',
            __dataIndex: 0,
            __encodeField: [y]
          }
        ],
        series: [
          {
            name: y,
            type: 'bar',
            data: yAxis.map(item => {
              return {
                value: item,
                itemStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    { offset: 0, color: 'rgba(77,89,153,0.5)' },
                    { offset: 1, color: 'rgba(77,89,153,0.24)' }
                  ])
                }
              };
            })
          },
          {
            type: 'custom',
            name: '↑',
            itemStyle: {
              borderWidth: 1
            },
            renderItem(params, api) {
              const xValue = api.value(0);
              const lowPoint = api.coord([xValue, api.value(2)]);
              const highPoint = api.coord([xValue, api.value(1)]);

              const barLayout = api.barLayout({
                barGap: '20%',
                barCategoryGap: '30%',
                count: 1
              });

              const interpoleHeight = Math.abs(highPoint[1] - lowPoint[1]);
              const maxHalfWidth = 8;
              const halfWidth = barLayout[0].width * 0.5;
              const arrowWidth = Math.min(maxHalfWidth, halfWidth);

              const isUp = api.value(2) < api.value(1);
              const defaultArrowHeight = (Math.sqrt(3) / 2) * arrowWidth;
              const arrowHeight = defaultArrowHeight;
              const isShowArrow = halfWidth > 2;

              const style = api.style({
                stroke: isUp ? '#ED2D00' : '#0B975C',
                fill: isUp ? '#ED2D00' : '#0B975C'
              });

              const lineStartStyle = api.style({
                stroke: isUp ? '#ED2D00' : '#0B975C',
                lineWidth: 2
              });

              const lineEndStyle = api.style({
                stroke: '#4D5999',
                lineWidth: 2
              });

              const barStyle = api.style({
                fill: isUp
                  ? rgbaToEquivalentRgb('rgba(237,45,0,0.16)')
                  : rgbaToEquivalentRgb('rgba(11,151,92,0.1)')
              });

              // Building the graphic directly within the `renderItem` function
              const graphic = {
                type: 'group',
                children: [
                  {
                    type: 'rect',
                    transition: ['shape'],
                    shape: {
                      x: Math.min(highPoint[0], lowPoint[0]) - halfWidth,
                      y: Math.min(highPoint[1], lowPoint[1]),
                      width: halfWidth * 2,
                      height: interpoleHeight
                    },
                    style: barStyle
                  },

                  {
                    type: 'line',
                    transition: ['shape'],
                    shape: {
                      x1: lowPoint[0] - halfWidth,
                      y1: lowPoint[1],
                      x2: lowPoint[0] + halfWidth,
                      y2: lowPoint[1]
                    },
                    style: lineStartStyle
                  },
                  {
                    type: 'line',
                    transition: ['shape'],
                    shape: {
                      x1: highPoint[0] - halfWidth,
                      y1: highPoint[1],
                      x2: highPoint[0] + halfWidth,
                      y2: highPoint[1]
                    },
                    style: lineEndStyle
                  }
                ]
              };

              // If the interpolation height is greater than arrow height, add a line between the two points
              if (interpoleHeight > arrowHeight) {
                graphic.children.push({
                  type: 'line',
                  transition: ['shape'],
                  shape: {
                    x1: highPoint[0],
                    y1: highPoint[1],
                    x2: lowPoint[0],
                    y2: lowPoint[1]
                  },
                  style
                });
              }

              // If arrow should be shown, add the arrow polygon
              if (isShowArrow) {
                graphic.children.push({
                  type: 'polygon',
                  show: isShowArrow,
                  transition: ['shape'],
                  shape: {
                    points: [
                      [
                        highPoint[0] - arrowWidth * 0.5,
                        isUp
                          ? interpoleHeight > arrowHeight
                            ? highPoint[1] + arrowHeight
                            : highPoint[1] + 2 * arrowHeight
                          : interpoleHeight > arrowHeight
                          ? highPoint[1] - arrowHeight
                          : highPoint[1] - 2 * arrowHeight
                      ],
                      [
                        highPoint[0] + arrowWidth * 0.5,
                        isUp
                          ? interpoleHeight > arrowHeight
                            ? highPoint[1] + arrowHeight
                            : highPoint[1] + 2 * arrowHeight
                          : interpoleHeight > arrowHeight
                          ? highPoint[1] - arrowHeight
                          : highPoint[1] - 2 * arrowHeight
                      ],
                      [
                        highPoint[0],
                        isUp
                          ? interpoleHeight > arrowHeight
                            ? highPoint[1]
                            : highPoint[1] + arrowHeight
                          : interpoleHeight > arrowHeight
                          ? highPoint[1]
                          : highPoint[1] - arrowHeight
                      ]
                    ]
                  },
                  style
                });
              }

              return graphic;
            },
            encode: {
              x: 0,
              y: [1, 2]
            },
            data: customData,
            z: 100
          },
          {
            type: 'custom',
            name: '↓'
          }
        ]
      };
    }
    // * -------------------------- chart
    return renderApi => {
      const legendColor = ['#4D5999', '#ED2D00', '#0B975C'];
      const renderParams = {
        option: renderApi.mergeChartOptionProxy(configureSeriesOption())
      };
      renderParams.option.legend.data = renderParams.option.legend.data.map((item, index) => {
        return {
          ...item,
          disableSelect: true,
          color: legendColor[index]
        };
      });
      renderApi.renderLegendProxy(renderParams.option.legend.data);
      renderApi.renderChartProxy(renderParams);
    };
  }
});
